/**
 * AI Navigator Controller
 * Contrôle l'agent IA qui navigue dans les données
 * Exécute de vraies analyses SQL pendant la navigation
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Play, Pause, RotateCcw, Zap } from 'lucide-react';
import { getBigDataBackendService } from '../../lib/big-data-backend';
import BigDataInsightsService from '../../services/BigDataInsightsService';
import { debugLog } from '../../utils/debug-logger';
import { useAuth } from '../auth/AuthProvider';
import { supabase } from '../../lib/supabase';
import logger from '../../utils/logger';

interface AINavigatorControllerProps {
  jobId: string;
  datasetId: string;
  totalRows: number;
  totalPages: number;
  pageSize: number;
  columns: Array<{ name: string; type: string }>;
  autoStart?: boolean; // ✅ NOUVEAU: Auto-démarrer la navigation
  onNavigationUpdate: (page: number, row: number) => void;
  onNavigationStart?: (isActive: boolean) => void; // ✅ NOUVEAU: Notifier quand navigation démarre/arrête
  onDiscovery: (discovery: {
    type: 'pattern' | 'anomaly' | 'insight';
    row: number;
    page: number;
    description: string;
  }) => void;
  onComplete?: () => void;
}

const AINavigatorController: React.FC<AINavigatorControllerProps> = ({
  jobId,
  datasetId,
  totalRows,
  totalPages,
  pageSize,
  columns,
  autoStart = false,
  onNavigationUpdate,
  onNavigationStart,
  onDiscovery,
  onComplete
}) => {
  const { user } = useAuth();
  const [isNavigating, setIsNavigating] = useState(false);
  const [currentRow, setCurrentRow] = useState(0);
  const [currentPhase, setCurrentPhase] = useState('');
  const [progress, setProgress] = useState(0); // 0-100
  const [speed, setSpeed] = useState(100); // lignes/seconde
  const [savedInsightsCount, setSavedInsightsCount] = useState(0);
  const [isLoadingState, setIsLoadingState] = useState(true);
  const isNavigatingRef = useRef(false);
  const currentRowRef = useRef(0); // ✅ FIX: Utiliser ref pour éviter stale closure
  const lastSaveTimeRef = useRef(0);

  // Phases de navigation
  // ✅ FIX: Les indices de lignes vont de 0 à totalRows-1
  const maxRowIndex = Math.max(0, totalRows - 1);
  const phases = [
    { name: 'Scan initial', rows: Math.min(5000, maxRowIndex), analysis: 'overview' },
    { name: 'Analyse statistique', rows: Math.min(10000, maxRowIndex), analysis: 'statistics' },
    { name: 'Détection patterns', rows: Math.min(20000, maxRowIndex), analysis: 'patterns' },
    { name: 'Recherche anomalies', rows: Math.min(50000, maxRowIndex), analysis: 'anomalies' },
    { name: 'Validation finale', rows: maxRowIndex, analysis: 'validation' }
  ];

  const backend = getBigDataBackendService();

  // ============================================
  // 💾 PERSISTANCE DE L'ÉTAT
  // ============================================

  /**
   * Sauvegarder l'état actuel dans Supabase (throttled à 1 fois/3s)
   */
  const saveNavigationState = useCallback(async (row: number, phase: string, progressPct: number) => {
    if (!user) return;
    
    const now = Date.now();
    if (now - lastSaveTimeRef.current < 3000) return; // Throttle 3s
    lastSaveTimeRef.current = now;

    try {
      await supabase
        .from('big_data_datasets')
        .update({
          metadata: {
            ai_navigator: {
              status: isNavigatingRef.current ? 'running' : 'paused',
              current_row: row,
              current_phase: phase,
              progress: progressPct,
              insights_found: savedInsightsCount,
              last_update: new Date().toISOString()
            }
          }
        })
        .eq('id', datasetId);
      
      debugLog('[💾 Navigator] State saved to Supabase');
    } catch (error) {
      logger.error('[❌ Navigator] Failed to save state:', error);
    }
  }, [user, datasetId, savedInsightsCount]);

  /**
   * Charger l'état sauvegardé depuis Supabase
   */
  const loadNavigationState = useCallback(async () => {
    if (!user) return;
    
    try {
      const { data, error } = await supabase
        .from('big_data_datasets')
        .select('metadata')
        .eq('id', datasetId)
        .single();

      if (!error && data?.metadata?.ai_navigator) {
        const nav = data.metadata.ai_navigator;
        const savedRow = nav.current_row || 0;
        setCurrentRow(savedRow);
        currentRowRef.current = savedRow; // ✅ FIX: Initialiser ref
        setCurrentPhase(nav.current_phase || '');
        setProgress(nav.progress || 0);
        setSavedInsightsCount(nav.insights_found || 0);
        
        // Si c'était en cours, proposer de reprendre
        if (nav.status === 'running') {
          debugLog('[🔄 Navigator] Previous session found, ready to resume');
        }
        
        debugLog('[✅ Navigator] State loaded from Supabase');
      }
    } catch (error) {
      logger.error('[❌ Navigator] Failed to load state:', error);
    } finally {
      setIsLoadingState(false);
    }
  }, [user, datasetId]);

  // Charger l'état au mount
  useEffect(() => {
    loadNavigationState();
  }, [loadNavigationState]);

  /**
   * 💾 Sauvegarder une découverte dans Supabase
   */
  const saveDiscoveryToSupabase = useCallback(async (
    discovery: { type: 'pattern' | 'anomaly' | 'insight'; row: number; page: number; description: string },
    category: 'pattern' | 'anomaly' | 'statistical',
    confidence: number
  ) => {
    if (!user) return;

    try {
      await BigDataInsightsService.createInsight({
        user_id: user.id,
        dataset_id: datasetId,
        type: discovery.type === 'insight' ? 'statistical' : discovery.type,
        category: discovery.type === 'anomaly' ? 'critical' : 'medium_value',
        confidence,
        title: discovery.description,
        description: `Découvert automatiquement par AI Navigator à la ligne ${discovery.row}`,
        row_start: discovery.row,
        row_end: discovery.row + 100,
        page_number: discovery.page,
        discovered_by: 'ai_navigator',
        discovered_at: new Date().toISOString()
      });
      
      setSavedInsightsCount(prev => prev + 1);
      debugLog('[✅ Navigator] Insight saved to Supabase');
    } catch (error) {
      logger.error('[❌ Navigator] Failed to save insight:', error);
    }
  }, [user, datasetId]);

  /**
   * Analyse une tranche de données
   */
  const analyzeSlice = useCallback(async (startRow: number, endRow: number, analysisType: string) => {
    try {
      // ⚠️ Désactivé en production pour éviter spam logs
      debugLog(`[AINavigator] Analyzing rows ${startRow}-${endRow} (${analysisType})`);
      
      // Construire query d'analyse basée sur le type
      let analysisQuery = '';
      
      switch (analysisType) {
        case 'overview':
          // Stats basiques
          analysisQuery = `
            SELECT 
              COUNT(*) as count,
              COUNT(DISTINCT ${columns[0]?.name || '*'}) as distinct_count
            FROM dataset 
            LIMIT ${endRow - startRow} OFFSET ${startRow}
          `;
          break;
          
        case 'statistics':
          // Colonnes numériques
          const numericCols = columns.filter(c => c.type === 'DOUBLE' || c.type === 'BIGINT' || c.type === 'INTEGER');
          if (numericCols.length > 0) {
            const col = numericCols[0].name;
            analysisQuery = `
              SELECT 
                AVG(${col}) as avg_val,
                MIN(${col}) as min_val,
                MAX(${col}) as max_val,
                STDDEV(${col}) as stddev_val
              FROM dataset
              LIMIT ${endRow - startRow} OFFSET ${startRow}
            `;
          }
          break;
          
        case 'patterns':
          // Group by sur première colonne texte
          const textCols = columns.filter(c => c.type === 'VARCHAR' || c.type === 'TEXT');
          if (textCols.length > 0) {
            const col = textCols[0].name;
            analysisQuery = `
              SELECT 
                ${col},
                COUNT(*) as frequency
              FROM dataset
              WHERE ${col} IS NOT NULL
              GROUP BY ${col}
              ORDER BY frequency DESC
              LIMIT 10 OFFSET ${startRow}
            `;
          }
          break;
          
        case 'anomalies':
          // Chercher des nulls ou valeurs extrêmes
          const numCol = columns.find(c => c.type === 'DOUBLE');
          if (numCol) {
            analysisQuery = `
              SELECT *
              FROM dataset
              WHERE ${numCol.name} IS NULL OR ${numCol.name} < 0
              LIMIT 10 OFFSET ${startRow}
            `;
          }
          break;
      }

      if (!analysisQuery) return;

      const result = await backend.executeQuery(jobId, analysisQuery);
      
      // Générer découverte basée sur les résultats
      if (result.rows && result.rows.length > 0) {
        const row = result.rows[0];
        let description = '';
        
        if (analysisType === 'overview') {
          description = `${row.count} enregistrements, ${row.distinct_count} valeurs uniques`;
          const discovery = {
            type: 'insight' as const,
            row: startRow,
            page: Math.floor(startRow / pageSize),
            description
          };
          onDiscovery(discovery);
          await saveDiscoveryToSupabase(discovery, 'statistical', 0.7);
        } else if (analysisType === 'statistics' && row.avg_val) {
          description = `Moyenne: ${parseFloat(row.avg_val).toFixed(2)}, Écart-type: ${parseFloat(row.stddev_val || 0).toFixed(2)}`;
          const discovery = {
            type: 'pattern' as const,
            row: startRow,
            page: Math.floor(startRow / pageSize),
            description
          };
          onDiscovery(discovery);
          await saveDiscoveryToSupabase(discovery, 'statistical', 0.8);
        } else if (analysisType === 'patterns') {
          description = `Pattern trouvé: "${row[Object.keys(row)[0]]}" (${row.frequency}x)`;
          const discovery = {
            type: 'pattern' as const,
            row: startRow,
            page: Math.floor(startRow / pageSize),
            description
          };
          onDiscovery(discovery);
          await saveDiscoveryToSupabase(discovery, 'pattern', 0.75);
        } else if (analysisType === 'anomalies' && result.rows.length > 0) {
          description = `Anomalie détectée: valeur manquante ou invalide`;
          const discovery = {
            type: 'anomaly' as const,
            row: startRow,
            page: Math.floor(startRow / pageSize),
            description
          };
          onDiscovery(discovery);
          await saveDiscoveryToSupabase(discovery, 'anomaly', 0.85);
        }
      }
    } catch (error) {
      debugLog('[AINavigator] Analysis error:', error);
    }
  }, [jobId, columns, pageSize, backend, onDiscovery]);

  /**
   * Boucle de navigation
   */
  const navigate = useCallback(async () => {
    console.log('🚀 [AINavigator] navigate() CALLED - Starting navigation loop');
    debugLog('[AINavigator] Starting navigation...');
    let row = currentRowRef.current; // ✅ FIX: Utiliser ref
    console.log('📍 [AINavigator] Starting from row:', row);
    
    for (const phase of phases) {
      if (!isNavigatingRef.current) {
        console.log('⛔ [AINavigator] Navigation stopped by user');
        break;
      }
      
      // Skip phases déjà complétées
      if (row >= phase.rows) {
        console.log(`⏭️ [AINavigator] Skipping phase ${phase.name} (row ${row} >= ${phase.rows})`);
        continue;
      }
      
      console.log(`🔍 [AINavigator] Starting phase: ${phase.name} (${row} -> ${phase.rows})`);
      debugLog(`[AINavigator] Phase: ${phase.name}`);
      setCurrentPhase(phase.name);
      
      const phaseEnd = Math.min(phase.rows, maxRowIndex); // ✅ FIX: Ne jamais dépasser maxRowIndex
      const step = Math.ceil((phaseEnd - row) / 20); // 20 analyses restantes
      
      while (row < phaseEnd && isNavigatingRef.current) {
        const nextRow = Math.min(row + step, phaseEnd);
        
        console.log(`📊 [AINavigator] Analyzing ${row} -> ${nextRow}`);
        
        // Analyser cette tranche
        await analyzeSlice(row, nextRow, phase.analysis);
        
        // Mettre à jour position
        row = nextRow;
        currentRowRef.current = nextRow; // ✅ FIX: Mettre à jour ref
        setCurrentRow(nextRow);
        const page = Math.floor(nextRow / pageSize);
        onNavigationUpdate(page, nextRow);
        
        // Calculer progression globale
        const progressPct = Math.round((nextRow / maxRowIndex) * 100); // ✅ FIX: Utiliser maxRowIndex
        setProgress(progressPct);
        
        console.log(`✅ [AINavigator] Progress: ${progressPct}% (row ${nextRow}/${maxRowIndex})`); // ✅ FIX: Log avec maxRowIndex
        
        // 💾 Sauvegarder état (throttled)
        await saveNavigationState(nextRow, phase.name, progressPct);
        
        // Pause pour animation
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Pause entre phases
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    console.log('🏁 [AINavigator] Navigation complete!');
    debugLog('[AINavigator] Navigation complete');
    setIsNavigating(false);
    isNavigatingRef.current = false;
    
    // Sauvegarder état final
    await saveNavigationState(row, 'Terminé', 100);
    
    // ✅ FIX: Notifier le parent
    if (onComplete) {
      console.log('✅ [AINavigator] Calling onComplete callback');
      onComplete();
    }
  }, [phases, totalRows, pageSize, analyzeSlice, onNavigationUpdate, saveNavigationState, onComplete]);

  /**
   * Démarrer/Arrêter
   */
  const toggleNavigation = useCallback(() => {
    console.log('🔥 [AINavigator] toggleNavigation called, isNavigating:', isNavigating);
    
    if (isNavigating) {
      console.log('⏸️ [AINavigator] Stopping navigation...');
      debugLog('[AINavigator] Stopping...');
      isNavigatingRef.current = false;
      setIsNavigating(false);
      
      // ✅ NOTIFIER LE PARENT: Navigation arrêtée
      if (onNavigationStart) {
        onNavigationStart(false);
      }
    } else {
      console.log('▶️ [AINavigator] Starting navigation...');
      debugLog('[AINavigator] Starting...');
      isNavigatingRef.current = true;
      setIsNavigating(true);
      
      // ✅ NOTIFIER LE PARENT: Navigation démarrée
      if (onNavigationStart) {
        console.log('📣 [AINavigator] Calling onNavigationStart(true)');
        onNavigationStart(true);
      }
      
      navigate();
    }
  }, [isNavigating, navigate, onNavigationStart]);

  /**
   * Reset
   */
  const reset = useCallback(() => {
    isNavigatingRef.current = false;
    setIsNavigating(false);
    setCurrentRow(0);
    currentRowRef.current = 0; // ✅ FIX: Reset ref
    setProgress(0);
    setCurrentPhase('');
    onNavigationUpdate(0, 0);
    
    // ✅ NOTIFIER LE PARENT: Navigation reset
    if (onNavigationStart) {
      onNavigationStart(false);
    }
  }, [onNavigationUpdate, onNavigationStart]);

  return (
    <div className="fixed bottom-6 right-6 z-40 max-w-sm">
      <div className="bg-gradient-to-r from-purple-900/90 to-indigo-900/90 backdrop-blur-md rounded-xl shadow-lg p-3 border border-purple-500/20">
        
        {/* Barre de progression */}
        {progress > 0 && (
          <div className="mb-3">
            <div className="flex justify-between text-xs text-purple-200 mb-1">
              <span>{currentPhase || 'En cours...'}</span>
              <span>{progress}%</span>
            </div>
            <div className="w-full bg-purple-900/50 rounded-full h-2 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-green-500 to-blue-500 h-full transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        <div className="flex items-center space-x-3">
          {/* Bouton Play/Pause */}
          <button
            onClick={toggleNavigation}
            disabled={isLoadingState}
            className={`p-2 rounded-lg transition-all ${
              isNavigating
                ? 'bg-red-500/90 hover:bg-red-600'
                : 'bg-green-500/90 hover:bg-green-600'
            } text-white shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {isNavigating ? (
              <Pause className="w-5 h-5" />
            ) : (
              <Play className="w-5 h-5" />
            )}
          </button>

          {/* Info compacte */}
          <div className="text-white flex-1">
            <div className="text-xs font-medium">
              {isNavigating ? '🔍 Analyse...' : currentRow > 0 ? '⏸️ Pause' : '✨ Prêt'}
            </div>
            <div className="text-xs text-purple-300 font-mono">
              {currentRow.toLocaleString()} / {totalRows.toLocaleString()}
            </div>
          </div>

          {/* Reset */}
          <button
            onClick={reset}
            disabled={isNavigating}
            className="p-2 rounded-lg bg-gray-700/80 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-white transition-all"
          >
            <RotateCcw className="w-4 h-4" />
          </button>

          {/* Vitesse */}
          <div className="flex items-center space-x-1.5 pl-3 border-l border-purple-500/20">
            <Zap className="w-3.5 h-3.5 text-yellow-400" />
            <span className="text-white text-xs font-mono">{speed}/s</span>
          </div>

          {/* Insights sauvegardés */}
          {savedInsightsCount > 0 && (
            <div className="flex items-center space-x-1.5 pl-3 border-l border-purple-500/20">
              <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-white text-xs">{savedInsightsCount}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AINavigatorController;

